import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { app } from '../../firebase/firebaseConfig';
import IglooMetricsViewer from './IglooMetricsViewer';

// /igloos/:iglooId/metrics — standalone, cold-bootable, shareable live metrics
// page for an igloo (mirrors /devices/:deviceId/remote). The URL is the source
// of truth: refresh and back/forward restore the same igloo, and the viewer is
// keyed on the id. The igloo name streams in after first paint — the viewer
// renders correctly with just the id meanwhile.
//
// This page reads NO igloo setting. It once fetched the videoBackend override to
// tell the viewer which metric namespace to read; the viewer now derives that from
// the metrics themselves, which is both truer (it reports what the session ran, not
// what it was configured for) and keeps per-setting knowledge out of the webapp.
function IglooMetricsPage() {
  const { iglooId } = useParams();
  const navigate = useNavigate();
  const [igloo, setIgloo] = useState(() => ({ id: iglooId }));

  // The igloos list may be scrolled deep when the metrics link is clicked; this
  // page should always open at the top. 'instant' because the app CSS sets
  // scroll-behavior smooth on <html>, which can swallow plain scrollTo calls.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [iglooId]);

  useEffect(() => {
    let cancelled = false;
    // Back/forward can land on a different igloo: drop the stale name now.
    setIgloo({ id: iglooId });
    const db = getFirestore(app);

    // The igloo's display name lives on the controller family's igloos
    // subcollection (familyInfo/{controller}/igloos/{iglooId}.name) — the
    // iglooInfo doc itself carries no name. Same resolution AdminDashboard uses.
    getDoc(doc(db, 'iglooInfo', iglooId))
      .then(async (snap) => {
        if (cancelled || !snap.exists()) return;
        const controllerId = (snap.data().controllers || [])[0];
        if (!controllerId) return; // no controller — the id stays as the title
        const iglooSnap = await getDoc(doc(db, 'familyInfo', controllerId, 'igloos', iglooId));
        if (!cancelled && iglooSnap.exists() && iglooSnap.data().name) {
          setIgloo({ id: iglooId, name: iglooSnap.data().name });
        }
      })
      .catch((e) => console.error(`IglooMetricsPage: igloo name load failed for ${iglooId}:`, e));

    return () => { cancelled = true; };
  }, [iglooId]);

  return (
    <IglooMetricsViewer
      key={iglooId}
      igloo={igloo}
      onBack={() => navigate('/igloos')}
    />
  );
}

export default IglooMetricsPage;
